import { useEffect, useState } from 'react';
import {
  listSuperAdmins, createSuperAdmin, toggleSuperAdmin, deleteSuperAdmin, SuperAdmin,
  listCompanies, getCompanyAccess, setCompanyAccess, Company,
} from '../services/api';
import { fmtDateJst, fmtDateTimeJst } from '../utils/datetime';
import PasswordInput from '../components/PasswordInput';

interface AddForm { name: string; email: string; password: string; sa_role: string; }

export default function SuperAdminsPage({ currentSaId }: { currentSaId: string }) {
  const [admins, setAdmins]   = useState<SuperAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>({ name: '', email: '', password: '', sa_role: 'admin' });
  const [adding, setAdding]   = useState(false);
  const [addError, setAddError] = useState('');
  const [toggling, setToggling] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // アクセス許可会社モーダル
  const [accessSa, setAccessSa]       = useState<SuperAdmin | null>(null);
  const [companies, setCompanies]     = useState<Company[]>([]);
  const [accessIds, setAccessIds]     = useState<Set<string>>(new Set());
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving]   = useState(false);
  const [accessError, setAccessError]     = useState('');
  const [accessCounts, setAccessCounts]   = useState<Record<string, number>>({});

  useEffect(() => {
    listSuperAdmins()
      .then(setAdmins)
      .catch(e => setError(e instanceof Error ? e.message : 'エラーが発生しました'))
      .finally(() => setLoading(false));
  }, []);

  async function openAccessModal(sa: SuperAdmin) {
    setAccessSa(sa); setAccessError(''); setAccessLoading(true);
    try {
      const [comps, access] = await Promise.all([
        companies.length ? Promise.resolve(companies) : listCompanies(),
        getCompanyAccess(sa.id),
      ]);
      setCompanies(comps);
      setAccessIds(new Set(access.company_ids));
    } catch (e) { setAccessError(e instanceof Error ? e.message : '読み込みに失敗しました'); }
    finally { setAccessLoading(false); }
  }

  function toggleAccessId(companyId: string) {
    setAccessIds(prev => {
      const next = new Set(prev);
      if (next.has(companyId)) next.delete(companyId); else next.add(companyId);
      return next;
    });
  }

  async function handleAccessSave() {
    if (!accessSa) return;
    setAccessSaving(true); setAccessError('');
    try {
      const ids = [...accessIds];
      await setCompanyAccess(accessSa.id, ids);
      setAccessCounts(prev => ({ ...prev, [accessSa.id]: ids.length }));
      setAccessSa(null);
    } catch (e) { setAccessError(e instanceof Error ? e.message : '保存に失敗しました'); }
    finally { setAccessSaving(false); }
  }

  async function handleAdd() {
    if (!addForm.name || !addForm.email || addForm.password.length < 6) {
      setAddError('氏名・メール・パスワード（6文字以上）は必須です。'); return;
    }
    setAdding(true); setAddError('');
    try {
      const created = await createSuperAdmin(addForm);
      setAdmins(prev => [...prev, created]);
      setAddForm({ name: '', email: '', password: '', sa_role: 'admin' });
      setShowAdd(false);
    } catch (e) { setAddError(e instanceof Error ? e.message : '追加に失敗しました'); }
    finally { setAdding(false); }
  }

  async function handleToggle(sa: SuperAdmin) {
    if (sa.id === currentSaId) { alert('自分自身を無効化することはできません。'); return; }
    if (!window.confirm(`「${sa.name}」を${sa.is_active ? '無効化' : '有効化'}しますか？`)) return;
    setToggling(sa.id);
    try {
      const updated = await toggleSuperAdmin(sa.id);
      setAdmins(prev => prev.map(a => a.id === sa.id ? { ...a, is_active: updated.is_active } : a));
    } catch (e) { alert(e instanceof Error ? e.message : 'エラーが発生しました'); }
    finally { setToggling(null); }
  }

  async function handleDelete(sa: SuperAdmin) {
    if (sa.id === currentSaId) { alert('自分自身を削除することはできません。'); return; }
    if (!window.confirm(`「${sa.name}」を削除します。\nこの操作は取り消せません。続けますか？`)) return;
    setDeleting(sa.id);
    try {
      await deleteSuperAdmin(sa.id);
      setAdmins(prev => prev.filter(a => a.id !== sa.id));
    } catch (e) { alert(e instanceof Error ? e.message : '削除に失敗しました'); }
    finally { setDeleting(null); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.3px' }}>運営管理者の管理</h1>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{admins.length} 名</span>
        </div>
        <button onClick={() => { setShowAdd(p => !p); setAddError(''); }} style={showAdd ? cancelBtnStyle : addBtnStyle}>
          {showAdd ? 'キャンセル' : '管理者を追加'}
        </button>
      </div>

      {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>{error}</div>}

      {/* 追加フォーム */}
      {showAdd && (
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#374151', marginBottom: 14 }}>新規運営管理者を追加</div>
          {addError && <div style={errorBoxStyle}>{addError}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* 新規登録フォームのため、ブラウザに保存されたログイン情報の自動入力を抑止する
                (email/password の組をログインフォームと誤認させない: autoComplete=off / new-password) */}
            <div>
              <label style={labelStyle}>氏名 *</label>
              <input name="new-sa-name" autoComplete="off" value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} placeholder="例: 田中 太郎" />
            </div>
            <div>
              <label style={labelStyle}>メールアドレス *</label>
              <input type="email" name="new-sa-email" autoComplete="off" value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} style={inputStyle} placeholder="例: tanaka@catms.jp" />
            </div>
            <div>
              <label style={labelStyle}>パスワード（6文字以上）*</label>
              <PasswordInput name="new-sa-password" autoComplete="new-password" value={addForm.password} onChange={e => setAddForm(p => ({ ...p, password: e.target.value }))} style={inputStyle} placeholder="初期パスワード" />
            </div>
            <div>
              <label style={labelStyle}>権限</label>
              <select value={addForm.sa_role} onChange={e => setAddForm(p => ({ ...p, sa_role: e.target.value }))} style={{ ...inputStyle, background: '#fff' }}>
                <option value="admin">管理者</option>
                <option value="owner">オーナー</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            オーナー権限は運営管理者の追加・削除や会社削除が可能です。慎重に設定してください。
          </div>
          <button onClick={handleAdd} disabled={adding} style={{ ...saveBtnStyle, marginTop: 14 }}>
            {adding ? '追加中...' : '追加する'}
          </button>
        </div>
      )}

      {/* 管理者一覧 */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>読み込み中...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['氏名', 'メールアドレス', '権限', 'ステータス', '最終ログイン', '登録日', 'アクセス許可会社', '操作'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: '#64748b', fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {admins.map(sa => (
                <tr key={sa.id} style={{ borderBottom: '1px solid #f1f5f9', opacity: sa.is_active ? 1 : 0.55 }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{sa.name}</div>
                    {sa.id === currentSaId && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>（自分）</div>}
                  </td>
                  <td style={tdStyle}>{sa.email}</td>
                  <td style={tdStyle}>
                    <span style={sa.sa_role === 'owner' ? ownerBadge : adminBadge}>
                      {sa.sa_role === 'owner' ? 'Owner' : '管理者'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={sa.is_active ? activeBadge : inactiveBadge}>{sa.is_active ? '有効' : '無効'}</span>
                  </td>
                  <td style={tdStyle}>{fmtDateTimeJst(sa.last_login_at, '未ログイン')}</td>
                  <td style={tdStyle}>{fmtDateJst(sa.created_at)}</td>
                  <td style={tdStyle}>
                    {sa.sa_role === 'owner' ? (
                      <span style={{ ...adminBadge, background: '#fef3c7', color: '#92400e' }}>全社（オーナー）</span>
                    ) : (
                      <button onClick={() => openAccessModal(sa)} style={accessBtnStyle}>
                        {accessCounts[sa.id] !== undefined ? `${accessCounts[sa.id]}社を許可` : 'アクセス許可を設定'}
                      </button>
                    )}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => handleToggle(sa)}
                      disabled={toggling === sa.id || sa.id === currentSaId}
                      style={sa.id === currentSaId ? disabledBtnStyle : sa.is_active ? disableBtnStyle : enableBtnStyle}
                      title={sa.id === currentSaId ? '自分自身は操作できません' : undefined}
                    >
                      {toggling === sa.id ? '…' : sa.is_active ? '無効化' : '有効化'}
                    </button>
                    <button
                      onClick={() => handleDelete(sa)}
                      disabled={deleting === sa.id || sa.id === currentSaId}
                      style={{ ...(sa.id === currentSaId ? disabledBtnStyle : deleteBtnStyle), marginLeft: 6 }}
                      title={sa.id === currentSaId ? '自分自身は削除できません' : '削除'}
                    >
                      {deleting === sa.id ? '…' : '削除'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* アクセス許可会社モーダル */}
      {accessSa && (
        <div style={overlayStyle} onClick={() => !accessSaving && setAccessSa(null)}>
          <div style={accessModalStyle} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 2 }}>アクセス許可会社</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              {accessSa.name} が運営管理者としてアクセスできる会社を選択します。
            </div>
            {accessError && <div style={errorBoxStyle}>{accessError}</div>}
            {accessLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>読み込み中...</div>
            ) : companies.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 14 }}>会社が登録されていません</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{accessIds.size} / {companies.length} 社を選択中</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => setAccessIds(new Set(companies.map(c => c.id)))} style={linkBtnStyle}>すべて選択</button>
                    <button onClick={() => setAccessIds(new Set())} style={linkBtnStyle}>すべて解除</button>
                  </div>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  {companies.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: 14 }}>
                      <input type="checkbox" checked={accessIds.has(c.id)} onChange={() => toggleAccessId(c.id)} style={{ width: 16, height: 16, accentColor: '#4f46e5' }} />
                      <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{c.subdomain}</span>
                      {!c.is_active && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#dc2626' }}>停止中</span>}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button onClick={handleAccessSave} disabled={accessSaving} style={{ ...saveBtnStyle, opacity: accessSaving ? 0.6 : 1 }}>
                    {accessSaving ? '保存中...' : '保存する'}
                  </button>
                  <button onClick={() => setAccessSa(null)} disabled={accessSaving} style={cancelBtnStyle}>キャンセル</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const addBtnStyle: React.CSSProperties    = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const cancelBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#475569', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const saveBtnStyle: React.CSSProperties   = { background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const labelStyle: React.CSSProperties    = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 };
const inputStyle: React.CSSProperties    = { width: '100%', border: '1.5px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const errorBoxStyle: React.CSSProperties = { background: '#fee2e2', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 12 };
const tdStyle: React.CSSProperties       = { padding: '12px 14px', color: '#374151', verticalAlign: 'middle' };
const ownerBadge: React.CSSProperties    = { background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 99 };
const adminBadge: React.CSSProperties    = { background: '#e0f2fe', color: '#0369a1', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99 };
const activeBadge: React.CSSProperties   = { background: '#dcfce7', color: '#16a34a', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 };
const inactiveBadge: React.CSSProperties = { background: '#f1f5f9', color: '#94a3b8', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 99 };
const disableBtnStyle: React.CSSProperties = { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const enableBtnStyle: React.CSSProperties  = { background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const disabledBtnStyle: React.CSSProperties = { background: '#f1f5f9', color: '#cbd5e1', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'not-allowed' };
const deleteBtnStyle: React.CSSProperties = { background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const accessBtnStyle: React.CSSProperties = { background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const linkBtnStyle: React.CSSProperties = { background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 };
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 };
const accessModalStyle: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.22)' };
